<template>
    <!-- SimpleCaptcha.vue (Vue 2) -->
    <div>
        <div class="captcha-row">
            <div class="simple-captcha" @click.prevent="generate">
                <canvas ref="canvas" :width="width" :height="height" class="captcha-canvas" />
            </div>
            <input
                v-model="inputValue"
                @input="onInputValueChange"
                placeholder="Nhập mã"
                :maxLength="length"
                class="captcha-input"
            />
        </div>
        <div v-if="errorMessage" class="captcha-error">{{ errorMessage }}</div>
    </div>
</template>

<script>
export default {
    name: 'SimpleCaptcha',
    props: {
        width: {
            type: Number,
            default: 160
        },
        height: {
            type: Number,
            default: 56
        },
        length: {
            type: Number,
            default: 5
        }
    },
    data() {
        return {
            text: '',
            errorMessage: "",
            inputValue: ''
        };
    },
    mounted() {
        this.generate();
    },
    methods: {
        // generate uppercase text (avoid ambiguous chars)
        _randomText() {
            const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
            let s = '';
            for (let i = 0; i < this.length; i++) {
                s += chars.charAt(Math.floor(Math.random() * chars.length));
            }
            return s;
        },

        // public: generate new captcha and clear input
        generate() {
            this.text = this._randomText();
            this.errorMessage = "";
            this.inputValue = '';
            this._draw();
        },

        // public: check input (case-insensitive)
        check() {
            if (
                this.inputValue.trim().toUpperCase() === this.text.trim().toUpperCase()
            ) {
                this.errorMessage = "";
                return true;
            } else {
                this.errorMessage = "Mã Captcha không đúng!";
                return false;
            }
        },
        onInputValueChange(e) {
            this.inputValue = e.target.value;
            this.errorMessage = "";
        },
        isValidLength(){
            return this.inputValue.length === this.length;
        },

        _draw() {
            const canvas = this.$refs.canvas;
            if (!canvas) return;
            const ctx = canvas.getContext('2d');
            const w = this.width;
            const h = this.height;

            // clear
            ctx.clearRect(0, 0, w, h);

            // background: soft gradient
            const g = ctx.createLinearGradient(0, 0, w, h);
            g.addColorStop(0, '#fbfbfb');
            g.addColorStop(1, '#f2f2f2');
            ctx.fillStyle = g;
            ctx.fillRect(0, 0, w, h);

            // add subtle noise dots
            for (let i = 0; i < Math.floor(w * h / 800); i++) {
                ctx.fillStyle = `rgba(${this._rand(60, 200)}, ${this._rand(60, 200)}, ${this._rand(60, 200)}, ${Math.random() * 0.35})`;
                ctx.beginPath();
                ctx.arc(this._rand(0, w), this._rand(0, h), this._rand(1, 2), 0, Math.PI * 2);
                ctx.fill();
            }

            // draw characters
            const len = this.text.length;
            const baseFontSize = Math.floor(h * 0.6);
            ctx.textBaseline = 'middle';
            for (let i = 0; i < len; i++) {
                const ch = this.text.charAt(i);
                const fontSize = baseFontSize + this._rand(-4, 4);
                ctx.font = `bold ${fontSize}px Arial, sans-serif`;

                // position
                const x = (w / (len + 1)) * (i + 1) + this._rand(-6, 6);
                const y = h / 2 + this._rand(-6, 6);

                // rotation small
                const angle = (Math.random() - 0.5) * (Math.PI / 12); // about ±15deg
                ctx.save();
                ctx.translate(x, y);
                ctx.rotate(angle);

                // stroke + fill for readability
                ctx.lineWidth = 1.2;
                ctx.strokeStyle = 'rgba(0,0,0,0.15)';
                ctx.strokeText(ch, -ctx.measureText(ch).width / 2, 0);
                ctx.fillStyle = `rgb(${this._rand(10, 80)}, ${this._rand(10, 80)}, ${this._rand(10, 80)})`;
                ctx.fillText(ch, -ctx.measureText(ch).width / 2, 0);
                ctx.restore();
            }

            // dashed lines (nét đứt) - light
            ctx.save();
            ctx.setLineDash([4, 3]);
            ctx.lineWidth = 1;
            for (let i = 0; i < 3; i++) {
                ctx.strokeStyle = `rgba(${this._rand(50, 160)},${this._rand(50, 160)},${this._rand(50, 160)},0.25)`;
                ctx.beginPath();
                ctx.moveTo(this._rand(0, w * 0.2), this._rand(0, h));
                ctx.quadraticCurveTo(this._rand(w * 0.2, w * 0.8), this._rand(0, h), this._rand(w * 0.8, w), this._rand(0, h));
                ctx.stroke();
            }
            ctx.restore();

            // small straight noise lines
            for (let i = 0; i < 5; i++) {
                ctx.beginPath();
                ctx.strokeStyle = `rgba(${this._rand(80, 200)},${this._rand(80, 200)},${this._rand(80, 200)},0.18)`;
                ctx.moveTo(this._rand(0, w), this._rand(0, h));
                ctx.lineTo(this._rand(0, w), this._rand(0, h));
                ctx.stroke();
            }
        },

        _rand(min, max) {
            return Math.floor(Math.random() * (max - min + 1)) + min;
        }
    }
};
</script>

<style scoped>
.captcha-row {
    display: flex;
    align-items: center;
    gap: 12px;
}
.simple-captcha {
    display: inline-block;
}
.captcha-canvas {
    border-radius: 6px;
    border: 1px solid #e6e6e6;
    cursor: pointer;
    display: block;
}
.captcha-input {
    padding: 8px;
    font-size: 22px;
    height:60px;
    width: 150px;
    border-radius: 6px;
    border: 1px solid #e6e6e6;
    outline: none;
    text-align: center;
    text-transform: uppercase;
}
.captcha-error {
    color: red;
    font-size: 16px;
    margin-top: 4px;
}
</style>


<!-- Usage Example (parent component - Vue 2) -->

<!--
  <template>
    <div>
      <div style="display:flex; align-items:center; gap:12px;">
        <input v-model="userInput" placeholder="Nhập mã" style="padding:8px; font-size:14px;" />
        <SimpleCaptcha ref="cap" v-model="captchaValue" />
      </div>
  
      <div style="margin-top:12px;">
        <button @click="verify">XÁC NHẬN</button>
        <button @click="$refs.cap.generate()" style="margin-left:8px;">Làm mới</button>
      </div>
    </div>
  </template>
  
  <script>
  import SimpleCaptcha from '@/components/SimpleCaptcha.vue';
  
  export default {
    components: { SimpleCaptcha },
    data() {
      return {
        captchaValue: '', // text hiện tại (in hoa)
        userInput: ''
      };
    },
    methods: {
      verify() {
        const ok = this.$refs.cap.check(this.userInput);
        if (ok) {
          alert('Captcha đúng ✅');
        } else {
          alert('Captcha sai ❌');
          this.$refs.cap.generate();
          this.userInput = '';
        }
      }
    }
  };
  </script>
  -->